import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, Loader2 } from "lucide-react";
import { useRag } from "../RagContext";
import "./DocumentUpload.css";

const DOCUMENT_TYPES = [
    { value: "bando_di_gara", label: "Bando di Gara" },
    { value: "disciplinare_tecnico", label: "Disciplinare Tecnico" },
    { value: "allegati_disciplinare", label: "Allegati al Disciplinare" },
    { value: "schema_di_contratto", label: "Schema di Contratto" },
    { value: "pronunciamenti_aters", label: "Pronunciamenti ATERSIR" },
    { value: "offerta_tecnica_rti", label: "Offerta Tecnica RTI" },
    { value: "allegati_offerta", label: "Allegati Offerta" },
    { value: "generico", label: "Documento Generico" },
];

export default function DocumentUpload() {
    const { currentBandoId, uploadDocument } = useRag();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [documentType, setDocumentType] = useState("");

    const onDrop = useCallback(
        async (acceptedFiles) => {
            if (!currentBandoId) {
                setError("Seleziona prima un bando.");
                return;
            }
            if (acceptedFiles.length === 0) return;

            setUploading(true);
            setError(null);
            try {
                for (const file of acceptedFiles) {
                    await uploadDocument(file, {
                        documentType: documentType || undefined,
                        bandoId: currentBandoId,
                    });
                }
            } catch (err) {
                setError(err?.response?.data?.error || err.message || "Errore nell'upload");
            } finally {
                setUploading(false);
            }
        },
        [currentBandoId, documentType, uploadDocument],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "application/pdf": [".pdf"],
            "text/plain": [".txt"],
        },
        disabled: uploading || !currentBandoId,
    });

    const dropzoneClass = [
        "rag-doc-upload__dropzone",
        isDragActive ? "rag-doc-upload__dropzone--active" : "",
        uploading || !currentBandoId ? "rag-doc-upload__dropzone--disabled" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="rag-doc-upload">
            <div className="rag-doc-upload__row">
                <label className="rag-doc-upload__label">Tipo documento (opzionale):</label>
                <select
                    className="rag-doc-upload__select"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    disabled={uploading}
                >
                    <option value="">— non specificato —</option>
                    {DOCUMENT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                            {t.label}
                        </option>
                    ))}
                </select>
            </div>

            <div {...getRootProps({ className: dropzoneClass })}>
                <input {...getInputProps()} />
                {uploading ? (
                    <div className="rag-doc-upload__inner">
                        <Loader2 className="rag-doc-upload__spinner" size={24} />
                        <p>Upload in corso…</p>
                    </div>
                ) : !currentBandoId ? (
                    <div className="rag-doc-upload__inner">
                        <Upload size={28} className="rag-doc-upload__icon" />
                        <p>Seleziona un bando per abilitare l'upload</p>
                    </div>
                ) : isDragActive ? (
                    <div className="rag-doc-upload__inner">
                        <Upload size={28} className="rag-doc-upload__icon" />
                        <p>Rilascia il file qui</p>
                    </div>
                ) : (
                    <div className="rag-doc-upload__inner">
                        <Upload size={28} className="rag-doc-upload__icon" />
                        <p>
                            Trascina qui i PDF o TXT, oppure <strong>clicca per scegliere</strong>
                        </p>
                        <span className="rag-doc-upload__hint">
                            Multi-file supportato. Limite 50 MB per file.
                        </span>
                    </div>
                )}
            </div>

            {error && <div className="rag-doc-upload__error">{error}</div>}
        </div>
    );
}
